/**
 * The transport. Everything that knows about SharePoint's HTTP quirks lives
 * here so the rest of the app sees promises and plain objects.
 *
 * Two quirks are load-bearing and were learned the hard way against the live
 * tenant:
 *
 *  - **Reads use `odata=nometadata`, writes use `odata=verbose`.** A POST that
 *    carries `{"@odata.type": ...}` under `nometadata` is rejected with a bare
 *    400; the same write with `__metadata: {type}` under `verbose` succeeds.
 *  - **A bearer token needs no `X-RequestDigest`.** That header is only for
 *    cookie-authenticated calls. Sending it with a token is harmless but
 *    pointless, and chasing a digest the app cannot get is a dead end.
 */

export interface TokenSource {
  /** Resolves a bearer token for the SharePoint resource. Called per request; cache upstream. */
  (): Promise<string>;
}

export type Fetch = typeof globalThis.fetch;

export interface HttpOptions {
  siteUrl: string;
  getToken: TokenSource;
  fetch?: Fetch;
}

export class SpError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    readonly detail: string,
  ) {
    super(`${status} on ${url}: ${detail}`);
    this.name = 'SpError';
  }
  /** Worth retrying: throttling and transient server faults, never a 4xx we caused. */
  get retryable(): boolean {
    return this.status === 429 || this.status === 503 || this.status >= 500;
  }
}

/** Escapes a value for an OData string literal. An unescaped quote is a broken query, not an error. */
export function odataString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

export class SpHttp {
  private readonly site: string;
  private readonly getToken: TokenSource;
  private readonly doFetch: Fetch;

  constructor(opts: HttpOptions) {
    this.site = opts.siteUrl.replace(/\/+$/, '');
    this.getToken = opts.getToken;
    this.doFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  get siteUrl(): string {
    return this.site;
  }

  private url(path: string): string {
    return path.startsWith('http') ? path : `${this.site}/_api/${path.replace(/^\/+/, '')}`;
  }

  private async headers(write: boolean): Promise<Record<string, string>> {
    const accept = write ? 'application/json;odata=verbose' : 'application/json;odata=nometadata';
    const h: Record<string, string> = {
      Accept: accept,
      Authorization: `Bearer ${await this.getToken()}`,
    };
    if (write) h['Content-Type'] = 'application/json;odata=verbose';
    return h;
  }

  private async send(url: string, init: RequestInit, write: boolean): Promise<Response> {
    const res = await this.doFetch(url, { ...init, headers: { ...(await this.headers(write)), ...(init.headers ?? {}) } });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.text()).slice(0, 500);
      } catch {
        detail = res.statusText;
      }
      throw new SpError(res.status, url, detail);
    }
    return res;
  }

  async get<T = unknown>(path: string): Promise<T> {
    const res = await this.send(this.url(path), { method: 'GET' }, false);
    return (await res.json()) as T;
  }

  /**
   * Follows `odata.nextLink` to the end.
   *
   * SharePoint pages silently at 100 rows by default. A caller that reads one
   * page and stops gets a register that looks complete and is not — which on
   * this platform means breakers nobody tests.
   */
  async getAll<T = unknown>(path: string, cap = 20_000): Promise<T[]> {
    const out: T[] = [];
    let next: string | undefined = this.url(path);
    while (next) {
      const page: { value?: T[]; 'odata.nextLink'?: string } = await this.get(next);
      if (page.value) out.push(...page.value);
      if (out.length > cap) throw new SpError(0, next, `refusing to read more than ${cap} rows`);
      next = page['odata.nextLink'];
    }
    return out;
  }

  /** Creates an item. `type` is the list's ListItemEntityTypeFullName. */
  async create<T = unknown>(path: string, type: string, fields: Record<string, unknown>): Promise<T> {
    const res = await this.send(
      this.url(path),
      { method: 'POST', body: JSON.stringify({ __metadata: { type }, ...fields }) },
      true,
    );
    const body = (await res.json()) as { d?: T };
    return (body.d ?? (body as unknown)) as T;
  }

  /** Merges fields into an existing item. Returns nothing: SharePoint answers 204. */
  async update(path: string, type: string, fields: Record<string, unknown>): Promise<void> {
    await this.send(
      this.url(path),
      {
        method: 'POST',
        headers: { 'X-HTTP-Method': 'MERGE', 'IF-MATCH': '*' },
        body: JSON.stringify({ __metadata: { type }, ...fields }),
      },
      true,
    );
  }

  /**
   * Uploads raw bytes, for `files/add`.
   *
   * SharePoint's file endpoints take the file as the request body, not as a JSON
   * payload — sending `{...}` there creates a file containing the literal text
   * of that object, which succeeds and is worse than failing.
   */
  async uploadBinary<T = unknown>(path: string, body: Blob | ArrayBuffer): Promise<T> {
    const res = await this.send(
      this.url(path),
      { method: 'POST', body: body as BodyInit, headers: { 'Content-Type': 'application/octet-stream' } },
      true,
    );
    const parsed = (await res.json()) as { d?: T };
    return (parsed.d ?? (parsed as unknown)) as T;
  }

  /** Sends an item to the recycle bin. Never a hard delete. */
  async recycle(path: string): Promise<void> {
    await this.send(this.url(`${path}/recycle()`), { method: 'POST' }, true);
  }
}
